import { Skeleton } from "@material-ui/lab";
import axios from "axios";
import moment from "moment-timezone";
import React, { useEffect, useMemo, useState } from "react";
import { OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { FaCircleInfo } from "react-icons/fa6";
import { MdOutlineErrorOutline } from "react-icons/md";
import { useSelector } from "react-redux";
import { useLanguage } from "../../../contexts/LanguageContext";
import { isParsable } from "../../../utils/commonUtils";
import { localizePrice } from "../../_helpers";
import CertificateLink from "../CertificateLink";
import { FETCH_CONSUMER_SHORTLIST_DATA } from "./../../../env_config";
import { ImagePopup } from "./../tables/imagePopup";
import { VideoPopup } from "./../tables/videoPopup";

// ============================================================
// MODULE-SCOPE HELPERS — defined once, not recreated per render
// ============================================================

const STATUS_ICON_CONFIG = {
    success: {
        Icon: FaCheckCircle,
        className: "text-success mr-2 font-size-h5",
    },
    warning: {
        Icon: MdOutlineErrorOutline,
        className: "text-warning mr-2 font-size-h3",
    },
    default: {
        Icon: FaTimesCircle,
        className: "text-danger mr-2 font-size-h5",
    },
};

const getDiamondTypeClass = (type) =>
    type === "L" ? "labTab" : type === "N" ? "natTab" : "";

const IconSpacer = () => (
    <span style={{ width: "14px" }}>
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
    </span>
);

// ============================================================
// STATUS COMPONENTS
// ============================================================

const StockStatus = React.memo(({ data }) => {
    const status = data.CURRENT_STATUS.status;
    const { Icon, className } =
        STATUS_ICON_CONFIG[status] || STATUS_ICON_CONFIG.default;

    return (
        <OverlayTrigger
            placement="left"
            overlay={
                <Tooltip id="tooltip">{data.CURRENT_STATUS.message}</Tooltip>
            }
        >
            <Icon className={className} />
        </OverlayTrigger>
    );
});

const StockStatusForOrderHistory = React.memo(({ data }) => {
    const language = useLanguage();
    const message = data.CURRENT_STATUS.message;

    if (message === "Purchased") {
        return (
            <div
                className="label label-outline-info label-pill label-inline bg-info-o-30 px-5"
                style={{ width: "max-content" }}
            >
                {language.CCMODE_CART_PURCHASED}
            </div>
        );
    }

    if (message === "Already Hold By You") {
        const isOutOfStock = Number(data.Status) === 1;
        return (
            <div
                className="label label-outline-warning label-pill label-inline bg-warning-o-30"
                style={{ width: "max-content" }}
            >
                {isOutOfStock && (
                    <OverlayTrigger
                        placement="top"
                        overlay={
                            <Tooltip id="tooltip">
                                {language.CCMODE_CART_OUT_OF_STOCK}
                            </Tooltip>
                        }
                    >
                        <FaTimesCircle className="text-danger mr-2 font-size-sm" />
                    </OverlayTrigger>
                )}
                {language.CCMODE_CART_BUY_REQUEST}
            </div>
        );
    }

    return null;
});

// ============================================================
// MEDIA / CERT CELL
// ============================================================

export const MediaCell = React.memo(({ val, language }) => (
    <div className="d-flex justify-content-center align-items-center">
        <span>
            {!val.AWS_Image ? (
                <IconSpacer />
            ) : (
                <OverlayTrigger
                    placement="left"
                    overlay={
                        <Tooltip id="tooltip">
                            {language.CCMODE_CART_IMAGE}
                        </Tooltip>
                    }
                >
                    <ImagePopup
                        Image={val.AWS_Image}
                        Shape={val.C_Shape}
                        sample={val?.sample}
                    />
                </OverlayTrigger>
            )}
        </span>
        <span>
            {!val.video ? (
                <IconSpacer />
            ) : (
                <OverlayTrigger
                    placement="left"
                    overlay={
                        <Tooltip id="tooltip">
                            {language.CCMODE_CART_VIDEO}
                        </Tooltip>
                    }
                >
                    <VideoPopup
                        ProductId={val.ProductId}
                        WLDiamondType={val.diamond_type}
                        CERTIFICATE_NO={val.CertiNo}
                        Video={val.video}
                    />
                </OverlayTrigger>
            )}
        </span>
        <span>
            {!val.CERTIFICATE_LINK ? (
                <IconSpacer />
            ) : (
                <OverlayTrigger
                    placement="left"
                    overlay={
                        <Tooltip id="tooltip">
                            {language.CCMODE_DIAMOND_DETAILS_CERTIFICATE}
                        </Tooltip>
                    }
                >
                    <CertificateLink
                        Certificate_link={val.CERTIFICATE_LINK}
                        lab={val.Lab}
                        certificate={val.Certi_No}
                        useIcon
                        className="text-primary mx-2 text-center"
                    />
                </OverlayTrigger>
            )}
        </span>
    </div>
));

// ============================================================
// PRICE CELLS
// ============================================================

const PriceCells = React.memo(({ val }) => {
    const language = useLanguage();
    const parsable = isParsable(val.SELECTED_CURRENCY_LIST_PRICE);

    return (
        <>
            <td className="text-center">{val.Currency}</td>
            <td className="text-right">
                {parsable ? (
                    <>{val.SELECTED_CURRENCY_LIST_PRICE_PER_CARAT_LABEL}</>
                ) : (
                    "-"
                )}
            </td>
            <td className="text-right" style={{ whiteSpace: "nowrap" }}>
                {parsable ? (
                    <>
                        {val.SELECTED_CURRENCY_LIST_PRICE_LABEL}
                        {val.taxBreakDown && (
                            <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Tooltip id="tax-breakdown-tooltip">
                                        {val.taxBreakDown}
                                    </Tooltip>
                                }
                            >
                                <span className="ml-2 d-inline-flex align-items-center cursor-pointer">
                                    <FaCircleInfo
                                        size={16}
                                        style={{ color: "#B5B5C3" }}
                                    />
                                </span>
                            </OverlayTrigger>
                        )}
                    </>
                ) : (
                    localizePrice(val.SELECTED_CURRENCY_LIST_PRICE, language)
                )}
            </td>
        </>
    );
});

// ============================================================
// TABLE HEADER
// ============================================================

const TableHeader = React.memo(
    ({ type, language, hideCertificateNumber, hidePrice }) => {
        const isOrderHistory = type === "order-history";
        return (
            <thead>
                <tr>
                    <th className="text-center text-nowrap">
                        {language.CCMODE_SHORTLISTPAGE_DIAMONDTYPE}
                    </th>
                    {isOrderHistory && (
                        <th className="text-center text-nowrap">
                            {language.CCMODE_CART_STATUS}
                        </th>
                    )}
                    <th className="text-center text-nowrap">
                        {language.CCMODE_THEME1_LIST_MEDIA}
                    </th>
                    <th className="text-center text-nowrap">
                        {language.CCMODE_SHORTLISTPAGE_STOCKNO}
                    </th>
                    {!hideCertificateNumber && (
                        <th className="text-center text-nowrap">
                            {language.CCMODE_DIAMOND_DETAILS_CERTIFICATE}
                        </th>
                    )}
                    <th className="text-center text-nowrap">
                        {language.CCMODE_DIAMOND_DETAILS_SHAPE}
                    </th>
                    <th className="text-center text-nowrap">
                        {language.CCMODE_DIAMOND_DETAILS_COLOR}
                    </th>
                    <th className="text-center text-nowrap">
                        {language.CCMODE_DIAMOND_DETAILS_CARAT}
                    </th>
                    <th className="text-center text-nowrap">
                        {language.CCMODE_DIAMOND_DETAILS_CUT}
                    </th>
                    <th className="text-center text-nowrap">
                        {language.CCMODE_DIAMOND_DETAILS_CLARITY}
                    </th>
                    <th className="text-center text-nowrap">
                        {language.CCMODE_DIAMOND_DETAILS_POLISH}
                    </th>
                    <th className="text-center text-nowrap">
                        {language.CCMODE_DIAMOND_DETAILS_SYMMETRY}
                    </th>
                    <th className="text-center text-nowrap">
                        {language.CCMODE_DIAMOND_DETAILS_FLOUR}
                    </th>
                    <th className="text-center text-nowrap">
                        {language.CCMODE_DIAMOND_DETAILS_LAB}
                    </th>
                    {!hidePrice && (
                        <>
                            <th className="text-center text-nowrap">
                                {language.CCMODE_SHORTLISTPAGE_CURRENCY}
                            </th>
                            <th className="text-center text-nowrap">$/CT</th>
                            <th className="text-center text-nowrap">
                                {language.CCMODE_SHORTLISTPAGE_PRICE}
                            </th>
                        </>
                    )}
                    <th className="text-center text-nowrap">
                        {language.CCMODE_THEME1_LIST_MEASUREMENT}
                    </th>
                    <th className="text-center text-nowrap">
                        {language.CCMODE_THEME1__LIST_DEPTH} %
                    </th>
                    <th className="text-center text-nowrap">
                        {language.CCMODE_THEME1__LIST_TABLE} %
                    </th>
                    <th className="text-center text-nowrap">
                        {language.CCMODE_SHORTLISTPAGE_CREATEDDATE}{" "}
                    </th>
                    {isOrderHistory && (
                        <th className="text-center text-nowrap">
                            {language.CCMODE_SHORTLISTPAGE_CLIENTREF}{" "}
                        </th>
                    )}
                </tr>
            </thead>
        );
    },
);

// ============================================================
// DATA ROW
// ============================================================

const DiamondRow = React.memo(
    ({ val, type, language, themeData, hideCertificateNumber, hidePrice }) => {
        const typeClass = getDiamondTypeClass(val.WLDiamondType);
        const isOrderHistory = type === "order-history";
        const timeZone = themeData?.timeZone || "Etc/GMT";

        return (
            <tr>
                <td className="text-center">
                    {val.WLDiamondType === "N"
                        ? language.CCMODE_CART_NATURAL
                        : language.CCMODE_CART_LAB}
                </td>
                {isOrderHistory && (
                    <td className="text-center">
                        <StockStatusForOrderHistory data={val} />
                    </td>
                )}
                <td className="text-center">
                    <MediaCell val={val} language={language} />
                </td>
                <td
                    className={`${typeClass} text-left pl-5 text-nowrap text-primary`}
                >
                    {!isOrderHistory && <StockStatus data={val} />}
                    {val.WLDiamondType}-{val.ProductId}
                </td>
                {!hideCertificateNumber && (
                    <td className="text-center text-nowrap">
                        <CertificateLink
                            Certificate_link={val.CERTIFICATE_LINK}
                            lab={val.Lab}
                            certificate={val.Certi_No}
                            hideLabName
                            className="text-primary mx-2 text-center"
                        />
                    </td>
                )}
                <td className="text-center text-nowrap">{val.C_Shape}</td>
                <td className="text-center">{val.C_Color}</td>
                <td className="text-center">{val.C_Weight.toFixed(2)}</td>
                <td className="text-center">{val.C_Cut}</td>
                <td className="text-center">{val.C_Clarity}</td>
                <td className="text-center">{val.C_Polish}</td>
                <td className="text-center">{val.C_Symmetry}</td>
                <td className="text-center">{val.C_Fluorescence}</td>
                <td className="text-center">{val.Lab}</td>
                {!hidePrice && <PriceCells val={val} />}
                <td className="text-center">
                    {parseFloat(val.C_Length).toFixed(2)}x
                    {parseFloat(val.C_Width).toFixed(2)}x
                    {parseFloat(val.C_Depth).toFixed(2)}
                </td>
                <td className="text-center">
                    {parseFloat(val.C_DefthP).toFixed(2)}
                </td>
                <td className="text-center">
                    {parseFloat(val.C_TableP).toFixed(2)}
                </td>
                <td className="text-center text-nowrap">
                    {val.CreatedAt
                        ? moment
                              .tz(val.CreatedAt, timeZone)
                              .format("YYYY-MM-DD HH:mm:ss")
                        : "-"}
                </td>
                {isOrderHistory && (
                    <td className="text-center text-nowrap">
                        {val.client_ref || ""}
                    </td>
                )}
            </tr>
        );
    },
);

// ============================================================
// SKELETON ROWS
// ============================================================

const SkeletonRows = React.memo(({ rows, cols }) => (
    <tbody>
        {Array.from({ length: rows <= 10 ? rows : 10 }).map((_, rowIdx) => (
            <tr key={rowIdx}>
                {Array.from({ length: cols }).map((_, colIdx) => (
                    <td key={colIdx} className="text-center">
                        <Skeleton animation="wave" />
                    </td>
                ))}
            </tr>
        ))}
    </tbody>
));

// ============================================================
// MAIN COMPONENT
// ============================================================

export const CartExpandedDetails = (data) => {
    // FIX: skeletonLength was a second function arg, which React never
    // populates. It must come from props.
    const { skeletonLength = 5 } = data;

    const user = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem("userData"));
        } catch {
            return null;
        }
    }, []);

    const { hidePrice, hideCertificateNumber } = useSelector(
        (state) => state.CcModeSettingReducer,
    );

    const [consumerData, setConsumerData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!user) return;

        const payload = JSON.stringify({
            customerId: user.CustomerId,
            ConsumerId: data.data.ConsumerId,
        });

        const url = `${FETCH_CONSUMER_SHORTLIST_DATA}${
            data.type === "order-history"
                ? "?purchaseHistoryWithBuyRequest=true"
                : "?removeBuyRequest=true"
        }`;

        setIsLoading(true);

        axios({
            method: "POST",
            url,
            headers: {
                "Access-Control-Allow-Origin": "*",
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            },
            data: payload,
        })
            .then((res) => {
                if (res.data.message === "UnAuthorized") {
                    localStorage.clear();
                    window.location.reload(true);
                    return;
                }
                setConsumerData(res.data.Result || []);
            })
            .catch((err) => {
                console.log(err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [user, data.data.ConsumerId, data.type]);

    // Compute column count from visibility flags instead of measuring
    // the DOM. This is reactive to redux changes and runs on first render
    // (the original needed an extra paint to populate via useRef).
    const colCount = useMemo(() => {
        let count = 16; // base columns always shown
        if (data.type === "order-history") count += 2;
        if (!hideCertificateNumber) count += 1;
        if (!hidePrice) count += 3;
        return count;
    }, [data.type, hideCertificateNumber, hidePrice]);

    const hasRecords = consumerData && consumerData.length > 0;

    return (
        <div className="sh_expand">
            <Table bordered responsive>
                <TableHeader
                    type={data.type}
                    language={data.language}
                    hideCertificateNumber={hideCertificateNumber}
                    hidePrice={hidePrice}
                />
                {isLoading ? (
                    <SkeletonRows rows={skeletonLength} cols={colCount} />
                ) : hasRecords ? (
                    <tbody>
                        {consumerData.map((val) => (
                            <DiamondRow
                                key={val.ProductId}
                                val={val}
                                type={data.type}
                                language={data.language}
                                themeData={data.themeData}
                                hideCertificateNumber={hideCertificateNumber}
                                hidePrice={hidePrice}
                            />
                        ))}
                    </tbody>
                ) : (
                    <tbody>
                        <tr>
                            <td
                                colSpan={colCount}
                                className="text-center text-capitalize"
                            >
                                {data.language.CCMODE_COMMON_NO_RECORD_FOUND}
                            </td>
                        </tr>
                    </tbody>
                )}
            </Table>
        </div>
    );
};

const OrderHistoryConsumerDetails = ({ data, language, themeData }) => {
    return (
        <div className="sh_expand">
            <Table bordered responsive>
                <thead>
                    <tr>
                        <th className="text-center text-nowrap">
                            {language.CCMODE_SHORTLISTPAGE_SHORTLISTNAME}
                        </th>
                        <th className="text-center text-nowrap">
                            {language.CCMODE_THEME3_CONSUMERNAME}
                        </th>
                        <th className="text-center text-nowrap">
                            {language.CCMODE_THEME3_EMAIL}
                        </th>
                        <th className="text-center text-nowrap">
                            {language.CCMODE_SHORTLISTPAGE_CONTACT_NUMBER}
                        </th>
                        <th className="text-center text-nowrap">
                            {language.CCMODE_SHORTLISTPAGE_NOOFSTONES}
                        </th>
                        <th className="text-center text-nowrap">
                            {language.CCMODE_SHORTLISTPAGE_TOTALCARAT}
                        </th>
                        <th className="text-center text-nowrap">
                            {language.CCMODE_SHORTLISTPAGE_NOTES}
                        </th>
                        <th className="text-center text-nowrap">
                            {language.CCMODE_SHORTLISTPAGE_CREATEDDATE}
                        </th>
                    </tr>
                </thead>

                <tbody>
                    <tr>
                        <td className="text-center">
                            {data?.ListName || "-"}
                        </td>
                        <td className="text-center">
                            {data?.ConsumerName || "-"}
                        </td>
                        <td className="text-center">
                            {data?.EMail || "-"}
                        </td>
                        <td className="text-center">
                            {data?.ContactNo || "-"}
                        </td>
                        <td className="text-center">
                            {data?.ShortListedStones ?? 0}
                        </td>
                        <td className="text-center">
                            {Number(data?.TotalCarats || 0).toFixed(2)}
                        </td>
                        <td className="text-center">
                            {data?.Notes || "-"}
                        </td>
                        <td className="text-center text-nowrap">
                            {data?.ListCreatedAt
                                ? moment
                                      .tz(
                                          data.ListCreatedAt,
                                          themeData?.timeZone || "Etc/GMT",
                                      )
                                      .format("YYYY-MM-DD HH:mm:ss")
                                : "-"}
                        </td>
                    </tr>
                </tbody>
            </Table>
        </div>
    )
};

export const ExpandedDetails = (props) => {
    if (props.type === "order-history") {
        return (
            <OrderHistoryConsumerDetails
                data={props.data}
                language={props.language}
                themeData={props.themeData}
            />
        );
    }

    return <CartExpandedDetails {...props} />;
};